"use strict";
/*!
Copyright (C) 2020 Evolved Binary

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractBaseNode = void 0;
exports.makeAll = makeAll;
exports.makeComponent = makeComponent;
const utils_1 = require("../utils");
const ast_classes_1 = require("../ast-classes");
const ast_utils_1 = require("../ast-utils");
/**
 * The base class for all nodes.
 */
class AbstractBaseNode {
    constructor(attributes) {
        if (attributes) {
            // `_props` - these are the attributes provided by the parser
            this._props = this.static.attributesToProps(attributes);
        }
    }
    /**
     * `attributesToProps` - Converts attributes to properties
     * Loops through all of the node attributes retrieved from the parser,
     * then gets their values from attributes,
     * and returns an object with all attributes an values.
     *
     * @param attributes - Attributes of the node
     * @returns An object with the record of properties
     */
    static attributesToProps(attributes = {}) {
        const result = {};
        // loop through all node attributes and get their values
        this.fields.forEach(field => {
            const attr = attributes[field];
            // `attr` can be a string or an object with value
            result[field] = typeof attr === 'string' ? attr : attr === null || attr === void 0 ? void 0 : attr.value;
        });
        return result;
    }
    /**
     * `isValidField` - This is a function template for validation of attributes
     *
     * @param field - A string containing the attribute name
     * @param value - A BasicValue-typed value containing the attribute value
     * @returns Boolean
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    static isValidField(field, value) {
        return true;
    }
    get static() {
        return this.constructor;
    }
    get children() {
        return this._children || [];
    }
    get json() {
        var _a;
        return {
            nodeName: this.static.nodeName,
            attributes: this._props,
            children: (_a = this._children) === null || _a === void 0 ? void 0 : _a.map(child => child.json),
        };
    }
    canAddNode(child) {
        const childNodeName = child.static.nodeName;
        return this.canAdd(childNodeName);
    }
    canAdd(childNodeName) {
        var _a;
        let childType;
        let iChild = -1;
        // loop through all of the allowed child types and check if the child node name is accepted
        // `this.static.childTypes`, e.g. allowed children: `['%list-blocks*', 'section*', 'fn*']`
        this.static.childTypes.some((type, i) => {
            childType = (0, utils_1.acceptsNodeName)(childNodeName, type, ast_utils_1.nodeGroups);
            if (childType) {
                iChild = i;
                return true;
            }
        });
        // If the child is not contained in the list `childTypes` it will be rejected
        if (!childType) {
            return false;
        }
        // get the last child of the parent nodename
        const last = ((_a = this.children) === null || _a === void 0 ? void 0 : _a.length) ? this.children[this.children.length - 1].static.nodeName : '';
        let iLast = -1;
        // if we do have a last child
        if (last) {
            // get the index of the last child in the list of allowed children
            iLast = this.static.childTypes.findIndex(type => (0, utils_1.acceptsNodeName)(last, type, ast_utils_1.nodeGroups));
            // if the child index is less than the last index, it can't be added
            // this ensures the correct and valid outline
            if (iLast > iChild) {
                return false;
            }
            // if the child index is equal to the last index, it can't be added if the child type is single
            // this ensure that there will be no duplication in an invalid manner
            if (iLast === iChild) {
                // if we have two of the same elements they can't be added
                // e.g. `<body>` and `<body>` within parent `<topic>`
                // you can tell if the element is single by checking the allowed children list `childNodes` and look for ? symbol
                if ((0, utils_1.isChildTypeSingle)(this.static.childTypes[iChild])) {
                    return false;
                }
                return true;
            }
        }
        // if the child index is greater than last index,
        // it can't be added if there are required child types between them
        const typesBetween = this.static.childTypes.slice(iLast + 1, iChild);
        // to check if a child type is required, check the allowed children list `childNodes`
        // and look for a child without `?` symbols at the end
        // e.g. `'title'` in this list: `['title', 'shortdesc?', 'prolog?', 'body?']`
        if (typesBetween.find(utils_1.isChildTypeRequired)) {
            return false;
        }
        return true;
    }
    add(child, breakOnError = true) {
        // If there are no children, initialize an empty array
        if (!this._children) {
            this._children = [];
        }
        // If there is a child that cannot be added, throw a new error
        if (!this.canAddNode(child)) {
            if (breakOnError) {
                throw new ast_classes_1.NonAcceptedChildError(`"${child.static.nodeName}" node can't be a child of "${this.static.nodeName}" node`);
            }
            return;
        }
        // Else add the child to the document tree
        this._children.push(child);
    }
    readProp(field) {
        if (this.static.fields.indexOf(field) < 0) {
            throw new ast_classes_1.UnknownAttributeError('unknown attribute "' + field + '"');
        }
        return this._props[field];
    }
    writeProp(field, value) {
        // If the attribute is identified as false then return error message
        if (this.static.fields.indexOf(field) < 0) {
            throw new ast_classes_1.UnknownAttributeError('unknown attribute "' + field + '"');
        }
        // If the attribute is known but doesn't match the element, return error message
        if (!this.static.isValidField(field, value)) {
            throw new ast_classes_1.WrongAttributeTypeError('wrong attribute type "' + typeof (value) + '" for field"' + field + '"');
        }
        // Else add attribute to element and document tree
        this._props[field] = value;
    }
    getProps() {
        return this._props;
    }
    allowsMixedContent() {
        return this.canAdd("text");
    }
}
exports.AbstractBaseNode = AbstractBaseNode;
// `nodeName` means node type (e.g. "image", "alt", "body", etc.)
AbstractBaseNode.nodeName = 'node';
/**
 * `makeAll` - This is a template function for all nodes
 *
 * @param constructor - The constructor
 * @param decorators - The decorator
 * @returns Instance of BaseNode
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeAll(constructor, ...decorators) {
    return decorators.reduce((result, decorator) => decorator(result), constructor);
}
/**
 * `makeComponent` -  A function that returns the constructor of a node
 *
 * @privateRemarks
 * The concept or processing of `decorator` is not yet fully understood
 *
 * @param decorator - The decorator
 * @param nodeName - A string containing the node name
 * @param fieldValidator -
 * @param fields - A List of attributes
 * @param childTypes - An Array of allowed child nodes
 * @returns The constructor function of an instance of BaseNode
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeComponent(decorator, nodeName, fieldValidator, fields, childTypes = []) {
    return (constructor) => {
        var _a;
        return decorator((_a = class extends constructor {
            },
            _a.nodeName = nodeName,
            _a.fields = fields,
            // These will get parsed when being set in the constructor
            _a.childTypes = (0, utils_1.stringToChildTypes)(childTypes),
            _a.isValidField = fieldValidator,
            _a));
    };
}
//# sourceMappingURL=base.js.map